import React, { useCallback, useContext, useState } from 'react';
import { FormContext } from '../../../../contexts/generated-content/content-form-context';
import { GreenroomState } from '../../../../stores/types/types';
import { useSelector } from 'react-redux';
import EditableFeaturedColor from '../../settings/color-pickers/color-swatch';
import {
	rectSortingStrategy,
	SortableContext,
} from '@dnd-kit/sortable';
import { DndContext, DragEndEvent, DragMoveEvent, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import ColorPicker from '../../settings/color-pickers/color-picker';
import { useColorPickerPosition } from '../../settings/color-pickers/util/use-color-picker-position';

const getDragKey = (hex: string, index: number) => `${hex} + ${index}`;
const PICKER_WIDTH = 270;
const COLUMNS_PER_ROW = 3;

const getIndexes = (e: DragEndEvent | DragMoveEvent) => {
	const { active, over } = e;
	const [activeColor, activeIndex] = active.id.split(' + ');
	const [overColor, overIndex] = (over?.id ?? 'none + -1').split(' + ');
	return [parseInt(activeIndex, 10), parseInt(overIndex, 10)];
}

interface Props {
	editColor: (color: string, index: number) => void;
	editedColors: string[]
	hasEdits: boolean
}

const basePickerData = {
	open: false,
	openHexAtStart: '',
	openListIndex: -1,
	domRect: null as null | DOMRect
}

export default function EditThemeColorTab(props: Props) {
	const {editColor, editedColors, hasEdits} = props;
	
	const sensors = useSensors(useSensor(MouseSensor));
	const viewedTheme = useSelector((s: GreenroomState) => s.ThemesReducer.viewedTheme);
	const {currTemplate, getTemplateSpecificStyling} = useContext(FormContext);
	
	const { containerRef, positionPicker } = useColorPickerPosition({
		pickerWidth: PICKER_WIDTH,
		offsetY: -54,
		offsetX: 28,
		strategy: 'auto'
	});
	
	const [colorPickerData, setColorPickerData] = useState(basePickerData);
	const [dragging, setDragging] = useState(false);

	const usedIndexes = React.useMemo(() => {
		const themeMap = viewedTheme?.title ? getTemplateSpecificStyling(currTemplate) : null;
		if (!themeMap) return [];
		return Object.values(themeMap.fieldToColorIndex);
	}, [viewedTheme?.title, currTemplate]);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		setDragging(false);
		const [activeIndex, overIndex] = getIndexes(event);
		if (overIndex === -1 || activeIndex === overIndex) return;

		setColorPickerData(basePickerData)
		
		const nColors: string[] = JSON.parse(JSON.stringify(editedColors));
		const color = nColors.splice(activeIndex, 1);
		nColors.splice(overIndex, 0, color[0]);

		nColors.map((c, i) => {editColor(c, i)});
	}, [editedColors, editColor]);

	const handleDragMove = useCallback((event: DragMoveEvent) => {
		const [activeIndex, overIndex] = getIndexes(event);
		if (overIndex === -1 || activeIndex === overIndex) return;

		setColorPickerData(basePickerData)
	}, [])
	
	const handlePositionPicker = useCallback((domRect: DOMRect, openListIndex: number, openHexAtStart: string) => {
		if (
			colorPickerData.openListIndex === openListIndex ||
			dragging
		) return;
		
		// Use the position hook to calculate and set position
		const position = positionPicker(domRect, openListIndex, COLUMNS_PER_ROW);
		
		if (!position) return;
		
		setColorPickerData({open: true, openHexAtStart, openListIndex, domRect});
	}, [dragging, colorPickerData.openListIndex, positionPicker]);

	const onChange = useCallback(({hex}: {hex:string}) => {
		editColor(hex, colorPickerData.openListIndex);
	}, [colorPickerData.openListIndex, editColor])

	const handleCloseColorPicker = useCallback(() => {
		setColorPickerData(curr => ({...curr, open: false, openListIndex: -1}));
	}, []);

	return (
		<>
			<DndContext 
				sensors={sensors} 
				onDragStart={() => setDragging(true)} 
				onDragEnd={handleDragEnd} 
				onDragMove={handleDragMove} 
				modifiers={[restrictToWindowEdges]}
			>
				<SortableContext items={editedColors.map(getDragKey)} strategy={rectSortingStrategy}>
					{editedColors.map((hex, index) => (
						<EditableFeaturedColor
							key={`Color ${index + hex}`}
							color={[`Color ${index + 1}`, [hex, 1]]}
							isOpen={colorPickerData.openListIndex === index}
							isDisabled={!hasEdits && !usedIndexes.includes(index)}
							dragKey={getDragKey(hex, index)}
							onShowColorPicker={(e) => handlePositionPicker(e, index, hex)}
						/>
					))}
				</SortableContext>
			</DndContext>

			{/* Position container managed by the hook */}
			<div 
				style={{position: 'fixed'}} 
				ref={containerRef} 
				id='color-picker-portal' 
				className='color-portal'
			/>

			<ColorPicker
				open={colorPickerData.open}
				openHexAtStart={colorPickerData.openHexAtStart}
				onChange={onChange}
				onClose={handleCloseColorPicker}
				portalId="color-picker-portal"
				pickerWidth={PICKER_WIDTH}
				customPosition={{top:90}}
				pickerClassname='color-picker-darker'
			/>
		</>
	);
}